"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UniversalKeys = exports.UniversalPasswordKey = exports.getKdfCacheMisses = exports.getKdfCacheHits = exports.clearKdfCache = exports.UniversalPrivateKey = exports.UniversalSymmetricKey = exports.UniversalKey = exports.defaultPKDOptions = exports.UniversalKeyException = void 0;
const unicrypto_1 = require("unicrypto");
const SimpleBoss_1 = require("./SimpleBoss");
const index_1 = require("./index");
/* istanbul ignore next */
class UniversalKeyException extends Error {
    constructor(text) {
        super(text);
    }
}
exports.UniversalKeyException = UniversalKeyException;
exports.defaultPKDOptions = {
    kdfAlgorithm: "PBKDF2",
    rounds: 50000,
    salt: unicrypto_1.randomBytes(32),
    hashAlgorithm: "sha256",
    idLength: 9,
    keyLength: 32,
    keyOffset: 9,
    kdfLength: 9 + 32
};
// inner constant. Some byte padding for RSA OAEP that we can use, but won't
// for extra security against some poisoning with chosen-ciphertext attacks (considered ineffective,
// but we aren't taking chances)
const MIN_PADDING = 3;
/* istanbul ignore next */
/**
 * Abstract UniversalKey
 */
class UniversalKey {
    /**
     * Encrypt with the key using some sort of ETA algorithm. Note that not all keys support
     * both encryption and decryption, though most of them should.
     *
     * @param plaintext
     */
    async encrypt(plaintext) {
        this.notApplicable();
    }
    /**
     * Decrypt with this key checking ETA used with [[encrypt]]. Note that not all keys support
     * both encryption and decryption, though most of them should.
     *
     * @param plaintext
     */
    async decrypt(plaintext) {
        this.notApplicable();
    }
    async sign(_message) {
        this.notApplicable();
    }
    async verify(_message, _signature) {
        this.notApplicable();
    }
    notApplicable() {
        throw new UniversalKeyException("operation is not applicable");
    }
}
exports.UniversalKey = UniversalKey;
/**
 * Universal symmetric key. Should be used with random tags, not derivable from the symmetric key. When
 * generating random, separate random value is used for a tag.
 */
class UniversalSymmetricKey extends UniversalKey {
    constructor(symmetricKey, tag) {
        super();
        this.key = symmetricKey;
        this.tag = { type: "symmetric", id: tag };
    }
    /**
     * Create a random key with a random tag. Because tags are serialized with keys, its a good solution
     * as good as the inner symmetric key is not used separately and the tag.id is not lost.
     */
    static createRandom() {
        return new UniversalSymmetricKey(new unicrypto_1.SymmetricKey(), unicrypto_1.randomBytes(32));
    }
    get keyBytes() {
        return this.key.pack();
    }
    async decrypt(ciphertext) {
        return await this.key.etaDecrypt(ciphertext);
    }
    async encrypt(plaintext) {
        return await this.key.etaEncrypt(plaintext);
    }
    async serialize() {
        return {
            tag: this.tag,
            packedKey: this.key.pack()
        };
    }
    static deserialize(serialized) {
        return new UniversalSymmetricKey(new unicrypto_1.SymmetricKey({ keyBytes: serialized.packedKey }), serialized.tag.id);
    }
    /**
     * Create a key from a binary key (in sense of AES) itself and a tag to associate with. Note that there is no
     * way to create cryptographically strong, e.g. independent, tag from key bytes so we do not try. The most (though
     * not ideally) safe approach is to derive a tag using KDF with many rounds, but it is slow, and not absolutely safe.
     *
     * @param keyBytes
     * @param keyTag if not specified, random tag will be assigned. This is potentially bad situation.
     */
    static fromKey(keyBytes, keyTag) {
        return new UniversalSymmetricKey(new unicrypto_1.SymmetricKey({ keyBytes: keyBytes }), keyTag !== null && keyTag !== void 0 ? keyTag : unicrypto_1.randomBytes(32));
    }
}
exports.UniversalSymmetricKey = UniversalSymmetricKey;
const rsaAlgorithm = "RSA-OAEP";
class UniversalPrivateKey extends UniversalKey {
    constructor(privateKey, options = {}) {
        super();
        this.options = { ...UniversalPrivateKey.defaultOptions };
        this.privateKey = privateKey;
        this.options = { ...this.options, ...options };
        this.publicKey = this.privateKey.publicKey;
        this.tag = { type: "private", id: this.publicKey.longAddress.bytes };
    }
    async serialize() {
        return {
            tag: this.tag,
            packedKey: await this.privateKey.pack(),
            algorithm: rsaAlgorithm,
            options: this.options
        };
    }
    static async deserialize(source) {
        var _a;
        /* istanbul ignore next */
        if (((_a = source === null || source === void 0 ? void 0 : source.algorithm) !== null && _a !== void 0 ? _a : rsaAlgorithm) != rsaAlgorithm)
            throw new UniversalKeyException("can't deserialize algorithm: " + source.algorithm);
        const key = await unicrypto_1.PrivateKey.unpack(source.packedKey);
        return new UniversalPrivateKey(key, { ...UniversalPrivateKey.defaultOptions, ...source === null || source === void 0 ? void 0 : source.options });
    }
    /**
     * How many bytes we can put inside PK encryption (depends on padding and key size)
     */
    async maxDataSize() {
        return this.privateKey.publicKey.encryptionMaxLength(this.options);
    }
    async decrypt(ciphertext) {
        const packed = SimpleBoss_1.bossLoad(ciphertext);
        switch (packed[0]) {
            case 0:
                return await this.privateKey.decrypt(packed[1], this.options);
            case 1: {
                const [ptk, chunk1] = SimpleBoss_1.bossLoad(await this.privateKey.decrypt(packed[1], this.options));
                const tk = new unicrypto_1.SymmetricKey({ keyBytes: ptk });
                const chunk2 = await tk.etaDecrypt(packed[2]);
                const result = new Uint8Array(chunk1.length + chunk2.length);
                result.set(chunk1, 0);
                result.set(chunk2, chunk1.length);
                return result;
            }
            default:
                /* istanbul ignore next */
                throw new UniversalKeyException("unknown encrypted record type" + packed[0]);
        }
    }
    async encrypt(plaintext) {
        // we suppose some padding should always be
        const maxDataSize = await this.maxDataSize();
        if (plaintext.length + MIN_PADDING < maxDataSize)
            // short and simple
            return SimpleBoss_1.bossDump([0, await this.publicKey.encrypt(plaintext, this.options)]);
        else {
            // long plaintext: encrypted temporary key
            const tk = new unicrypto_1.SymmetricKey();
            const packedTk = tk.pack();
            const chunk1Size = maxDataSize - packedTk.length - MIN_PADDING - 3;
            const chunk1 = plaintext.slice(0, chunk1Size);
            const chunk2 = plaintext.slice(chunk1Size);
            return SimpleBoss_1.bossDump([
                1,
                await this.publicKey.encrypt(SimpleBoss_1.bossDump([packedTk, chunk1]), this.options),
                await tk.etaEncrypt(chunk2)
            ]);
        }
    }
    /**
     * Asynchronously generate new private key in the worker thread.
     *
     * @param strength in bits, e.g. 2048
     */
    static async generate(strength = 4096) {
        return new UniversalPrivateKey(await unicrypto_1.PrivateKey.generate({ strength: strength }));
    }
}
exports.UniversalPrivateKey = UniversalPrivateKey;
UniversalPrivateKey.defaultOptions = {
    mgf1Hash: "sha256",
    oaepHash: "sha256",
    pssHash: "sha3_384"
};
const kdfSpaces = new Map();
let kdfCacheHits = 0;
let kdfCacheMisses = 0;
/**
 * Strong delete caches. First fill the spaces with 0, then with 1, then release buffers.
 */
function clearKdfCache() {
    for (const v of kdfSpaces.values()) {
        for (let i = 0; i < v.length; i++)
            v[i] = 0;
        for (let i = 0; i < v.length; i++)
            v[i] = 255;
    }
    kdfCacheHits = 0;
    kdfCacheMisses = 0;
    kdfSpaces.clear();
}
exports.clearKdfCache = clearKdfCache;
function getKdfCacheHits() {
    return kdfCacheHits;
}
exports.getKdfCacheHits = getKdfCacheHits;
function getKdfCacheMisses() {
    return kdfCacheMisses;
}
exports.getKdfCacheMisses = getKdfCacheMisses;
/**
 * Checks params sanity, generates KDF space and caches it for multi-key scenario. Does not generate
 * KDF bytes it there are cached one.
 *
 * @param password to derive from
 * @param params to generate from
 * @return KDF generated bytes
 */
async function getKDFData(password, params) {
    // we need password for cache key but we dont want to use it:
    const scrambledPwd = unicrypto_1.encode64(await index_1.sha256(password));
    const key = `${params.kdfAlgorithm}:${params.rounds}:${params.kdfLength}:${params.hashAlgorithm}:${unicrypto_1.encode64(params.salt)}:${scrambledPwd}`;
    let data = kdfSpaces.get(key);
    if (!data) {
        /* istanbul ignore next */
        if (params.kdfAlgorithm != "PBKDF2")
            throw new Error("unsupported KDF function " + params.kdfAlgorithm);
        /* istanbul ignore next */
        if (params.keyOffset < params.idLength)
            throw new Error("inconsistent KDF data: key overlaps the id");
        /* istanbul ignore next */
        if (params.keyOffset + params.kdfLength < params.kdfLength)
            throw new Error("inconsistent KDF data: key is not contained in the kdfSpace");
        /* istanbul ignore next */
        if (params.idLength < 1 || params.keyLength < 4)
            throw new Error("illegal length parameters");
        // "regular" hash constants are like "sha256" but SHA constructor needs not the prefix
        // otherwise uses same notation:
        data = await unicrypto_1.pbkdf2(params.hashAlgorithm, {
            rounds: params.rounds,
            keyLength: params.kdfLength,
            password: password,
            salt: params.salt
        });
        kdfSpaces.set(key, data);
        kdfCacheMisses++;
    }
    else
        kdfCacheHits++;
    return data;
}
class UniversalPasswordKey extends UniversalKey {
    /**
     * Construct a password key either from a password and optional pkdOptions or by deserialize it.
     * We recommend using class-methods for clarity.
     *
     * @param options
     */
    constructor(options) {
        super();
        this.key = this.deriveKey(options);
    }
    /**
     * Derive a key from a password optionally using KD options. Automatically uses cached KDF
     * space if it was already calculated.
     *
     * @param password
     * @param pkdOptions
     */
    static async deriveFrom(password, pkdOptions) {
        const key = new UniversalPasswordKey({ password, pkdOptions });
        await key.waitDerived();
        return key;
    }
    async deriveKey(options) {
        if (options.password) {
            const params = { ...exports.defaultPKDOptions, ...options.pkdOptions };
            /* istanbul ignore next */
            if (params.kdfAlgorithm != "PBKDF2")
                throw new Error("unsupported KDF function " + params.kdfAlgorithm);
            const bytes = await getKDFData(options.password, params);
            this.tag = {
                type: "password",
                id: bytes.slice(0, params.idLength),
                pkdOptions: { ...params }
            };
            return new unicrypto_1.SymmetricKey({
                keyBytes: bytes.slice(params.keyOffset, params.keyOffset + params.keyLength)
            });
        }
        else if (options.serialized) {
            this.tag = options.serialized.tag;
            return new unicrypto_1.SymmetricKey({ keyBytes: options.serialized.packedKey });
        }
        else {
            throw new UniversalKeyException("not consistent password key constructor");
        }
    }
    async decrypt(ciphertext) {
        return await (await this.key).etaDecrypt(ciphertext);
    }
    get symmetricKey() {
        return this.key;
    }
    async encrypt(plaintext) {
        return await (await this.key).etaEncrypt(plaintext);
    }
    async waitDerived() {
        await this.symmetricKey;
    }
    async serialize() {
        // ensure it is derived
        await this.waitDerived();
        return {
            tag: this.tag,
            packedKey: (await this.key).pack()
        };
    }
    /**
     * Create several keys derived from the same password using wide output from the single KDF invocation.
     * Keys are placed adjacent to the id area. The KDF function when using any of these keys is calculated
     * only once.
     *
     * @param password to derive from
     * @param count how many keys to create
     * @param options optional options.
     */
    static async deriveMultiple(password, count, options) {
        const o = { ...exports.defaultPKDOptions, ...options, keySize: 32 };
        const opts = { ...o, kdfLength: 32 * count + o.idLength };
        const keys = new Array();
        for (let i = 0; i < count; i++) {
            // we want to avoid unnecessary recalculations in parallel threads. to make cache
            // work we should not start KDF in parallel (what constructor actually does). So
            // constructor start derivation:
            const k = await UniversalPasswordKey.deriveFrom(password, {
                ...opts,
                keyOffset: opts.idLength + i * opts.keyLength
            });
            // forcing await derivation done and cache warmed:
            // then save it and go further
            keys.push(k);
        }
        return keys;
    }
}
exports.UniversalPasswordKey = UniversalPasswordKey;
/**
 * Universal keys utilities
 */
class UniversalKeys {
    /**
     * Load from serialized object. {@linkcode UniversalKey.serialize}
     *
     * @param source object (key-value) holding serialized key or boss-packed binary.
     */
    static async loadFrom(source) {
        const serialized = source instanceof Uint8Array ? SimpleBoss_1.bossLoad(source) : source;
        switch (serialized.tag.type) {
            case "symmetric":
                return UniversalSymmetricKey.deserialize(serialized);
            case "private":
                return UniversalPrivateKey.deserialize(serialized);
            case "password":
                return new UniversalPasswordKey({ serialized: serialized });
            default:
                throw new UniversalKeyException("unsupported serialized key type: " + serialized.tag.type);
        }
    }
}
exports.UniversalKeys = UniversalKeys;
//# sourceMappingURL=UnversalKeys.js.map